import { SurveyAnswers, SurveyCard, SurveyCell } from "types/surveyTest.types"
import { AcceptedScreen, cardTitle, isBlank, openOf } from "./session"

/** One line of the Answered tab: a section, a question answered in it, or one row of a grid's answer. */
export interface AnsweredNode {
  kind: "section" | "question" | "answer"
  key: string
  /** The section as written, the card's title, or a grid row's text ("row, column" in a GRID FILL IN) */
  title: string
  /** The answer as the card showed it; "" for a section, a grid's question line and a blank cell */
  answer: string
  children: AnsweredNode[]
}

/** The text of the item with `id` in `list`, or the id when the card does not describe it; an empty text shows the id. */
const textIn = (list: { id: string; text: string }[], id: string): string =>
  list.find((x) => x.id === id)?.text || id

/** A chosen option's text, with its typed text in quotes when it asks to specify: Other: "My brand". */
const optionAnswer = (card: SurveyCard, cell: SurveyCell, option: string, values: SurveyAnswers): string => {
  const text = textIn(card.options, option)
  const open = openOf(cell, option)
  const typed = open === undefined ? undefined : values[open]
  return open !== undefined && !isBlank(typed) ? `${text}: "${String(typed)}"` : text
}

/** A cell's answer as the card showed it: the option texts chosen, comma-separated, or the value as typed. */
const cellAnswer = (card: SurveyCard, cell: SurveyCell, values: SurveyAnswers): string => {
  const v = values[cell.id]
  if (isBlank(v)) return ""
  if (card.kind === "single" || card.kind === "multi")
    return (Array.isArray(v) ? v : [String(v)]).map((option) => optionAnswer(card, cell, option, values)).join(", ")
  return Array.isArray(v) ? v.join(", ") : String(v)
}

/** A grid row's name: its text, and in a GRID FILL IN the column's after a comma. */
const cellTitle = (card: SurveyCard, cell: SurveyCell): string => {
  const row = textIn(card.rows, cell.row) || cell.id
  return cell.column ? `${row}, ${textIn(card.columns, cell.column)}` : row
}

/** A card answered on a screen: a plain question carries its answer, a grid one line per answered row. */
const questionNode = (card: SurveyCard, key: string, values: SurveyAnswers): AnsweredNode => {
  const plain = card.layout === "plain"
  const first = card.cells[0]
  return {
    kind: "question",
    key: `q${key}`,
    title: cardTitle(card),
    answer: plain && first ? cellAnswer(card, first, values) : "",
    children: plain
      ? []
      : card.cells.flatMap((cell, i) => {
          const answer = cellAnswer(card, cell, values)
          return answer ? [{ kind: "answer" as const, key: `a${key}.${i}`, title: cellTitle(card, cell), answer, children: [] }] : []
        }),
  }
}

/**
 * The screens accepted so far as a tree: section, question, answer, in the order they were answered. Cards
 * in a row with the same section share its node; a card without a section sits at the top level. An info
 * card answers nothing and is left out.
 */
export const answeredTree = (history: AcceptedScreen[]): AnsweredNode[] => {
  const top: AnsweredNode[] = []
  let section: AnsweredNode | undefined
  history.forEach((entry, screen) =>
    entry.cards.forEach((card, at) => {
      if (card.kind === "info") return
      const key = `${screen}.${at}`
      const node = questionNode(card, key, entry.values)
      if (!card.section) {
        section = undefined
        top.push(node)
        return
      }
      if (section?.title !== card.section) {
        section = { kind: "section", key: `s${key}`, title: card.section, answer: "", children: [] }
        top.push(section)
      }
      section.children.push(node)
    })
  )
  return top
}
